import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  FileText, ArrowLeft, Loader2, Save, RefreshCw, Image, Eye, X
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BlogContentEditor } from '@/components/admin/BlogContentEditor';
import { BlogImageGalleryManager, BlogMediaItem } from '@/components/admin/BlogImageGalleryManager';

interface BlogPost {
  id: string;
  site_id: string;
  slug: string;
  title: string;
  category: string;
  topic: string;
  content_html: string;
  excerpt: string | null;
  main_image_url: string | null;
  main_image_alt: string | null;
  image_source: string | null;
  image_source_url: string | null;
  image_attribution_text: string | null;
  prefer_real_images: boolean;
  published_at: string;
  is_published: boolean;
}

interface Tag {
  id: string;
  name: string;
}

const CATEGORIES = [
  'Buyers', 'Sellers', 'Investor', 'Local Market and Geography',
  'Lifestyle and Community', 'Food and Drink', 'Family and Kids',
  'Education and Schools', 'Health and Wellness', 'Work, Business and Economy',
  'Housing and Homeownership', 'Legal, Financial and Planning', 'Relocation and Moving',
  'Community and Civic', 'Seasonal and Time Based', 'Trust and Authority',
  'Buyer and Seller Journey Stage',
];
import { useRef } from 'react';

export default function BlogEdit() {
  const { postId } = useParams<{ postId: string }>();
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, isGlobalAdmin, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [post, setPost] = useState<BlogPost | null>(null);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regeneratingContent, setRegeneratingContent] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [uploadingFeaturedImage, setUploadingFeaturedImage] = useState(false);
  const [contentImages, setContentImages] = useState<BlogMediaItem[]>([]);
  const featuredImageInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    topic: '',
    content_html: '',
    excerpt: '',
    main_image_url: '',
    main_image_alt: '',
    image_source: 'ai_generated',
    image_source_url: '',
    image_attribution_text: '',
    prefer_real_images: true,
    published_at: '',
    is_published: true,
  });
  const [overrideTown, setOverrideTown] = useState('');
  const [towns, setTowns] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
    if (user && postId && !globalAdminLoading && selectedSiteId) {
      fetchPost();
    }
  }, [user, authLoading, postId, globalAdminLoading, selectedSiteId, navigate]);

  const fetchPost = async () => {
    if (!selectedSiteId) {
      navigate('/admin');
      return;
    }

    try {
      const [postRes, tagsRes, postTagsRes, townsRes] = await Promise.all([
        supabase.from('blog_posts').select('*').eq('id', postId).eq('site_id', selectedSiteId).single(),
        supabase.from('blog_tags').select('id, name').order('name'),
        supabase.from('blog_post_tags').select('tag_id').eq('blog_post_id', postId),
        supabase.from('towns').select('id, name').eq('site_id', selectedSiteId).order('name')
      ]);

      if (!postRes.data) {
        toast({ title: 'Post not found', variant: 'destructive' });
        navigate('/admin/blog');
        return;
      }

      const postData = postRes.data as BlogPost;
      setPost(postData);
      setFormData({
        title: postData.title,
        category: postData.category,
        topic: postData.topic,
        content_html: postData.content_html,
        excerpt: postData.excerpt || '',
        main_image_url: postData.main_image_url || '',
        main_image_alt: postData.main_image_alt || '',
        image_source: postData.image_source || 'ai_generated',
        image_source_url: postData.image_source_url || '',
        image_attribution_text: postData.image_attribution_text || '',
        prefer_real_images: postData.prefer_real_images !== false,
        published_at: format(new Date(postData.published_at), "yyyy-MM-dd'T'HH:mm"),
        is_published: postData.is_published,
      });

      if (tagsRes.data) setAllTags(tagsRes.data);
      if (postTagsRes.data) setSelectedTagIds(postTagsRes.data.map(t => t.tag_id));
      if (townsRes.data) setTowns(townsRes.data);

    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagToggle = (tagId: string) => {
    setSelectedTagIds(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSave = async () => {
    if (!post) return;

    setSaving(true);
    try {
      // Update post
      const { error: postError } = await supabase
        .from('blog_posts')
        .update({
          title: formData.title,
          category: formData.category,
          topic: formData.topic,
          content_html: formData.content_html,
          excerpt: formData.excerpt || null,
          main_image_url: formData.main_image_url || null,
          main_image_alt: formData.main_image_alt || null,
          image_source: formData.image_source || null,
          image_source_url: formData.image_source_url || null,
          image_attribution_text: formData.image_attribution_text || null,
          prefer_real_images: formData.prefer_real_images,
          published_at: new Date(formData.published_at).toISOString(),
          is_published: formData.is_published,
        })
        .eq('id', post.id);

      if (postError) throw postError;

      // Update tags - delete existing and insert new
      await supabase.from('blog_post_tags').delete().eq('blog_post_id', post.id);
      
      if (selectedTagIds.length > 0) {
        const tagInserts = selectedTagIds.map(tagId => ({
          blog_post_id: post.id,
          tag_id: tagId,
        }));
        await supabase.from('blog_post_tags').insert(tagInserts);
      }

      toast({
        title: 'Saved',
        description: 'Blog post has been updated.',
      });
    } catch (error) {
      console.error('Error saving post:', error);
      toast({
        title: 'Error',
        description: 'Failed to save post.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateContent = async () => {
    if (!post) return;

    setRegeneratingContent(true);
    try {
      const { data, error } = await supabase.functions.invoke('regenerate-blog-content', {
        body: { postId: post.id, field: 'content' }
      });

      if (error) throw error;

      if (data) {
        setFormData(prev => ({
          ...prev,
          title: data.title || prev.title,
          content_html: data.content_html || prev.content_html,
          excerpt: data.excerpt || prev.excerpt,
        }));
        if (data.tagIds) setSelectedTagIds(data.tagIds);
      }

      toast({
        title: 'Content regenerated',
        description: 'The content has been updated. Remember to save.',
      });
    } catch (error) {
      console.error('Error regenerating content:', error);
      toast({
        title: 'Error',
        description: 'Failed to regenerate content.',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingContent(false);
    }
  };

  const handleRegenerateImage = async (forceAI = false) => {
    if (!post) return;

    setRegeneratingImage(true);
    try {
      const { data, error } = await supabase.functions.invoke('regenerate-blog-content', {
        body: { 
          postId: post.id, 
          field: 'image',
          forceAI,
          overrideTown: overrideTown || undefined
        }
      });

      if (error) throw error;

      if (data?.main_image_url) {
        setFormData(prev => ({
          ...prev,
          main_image_url: data.main_image_url,
          main_image_alt: data.main_image_alt || prev.main_image_alt,
          image_source: data.image_source || 'ai_generated',
          image_source_url: data.image_source_url || '',
          image_attribution_text: data.image_attribution_text || '',
        }));
      }

      const sourceLabel = data?.image_source === 'unsplash' ? 'Unsplash' : 
                          data?.image_source === 'pexels' ? 'Pexels' : 'AI';
      toast({
        title: 'Image regenerated',
        description: `New image sourced from ${sourceLabel}. Remember to save.`,
      });
    } catch (error) {
      console.error('Error regenerating image:', error);
      toast({
        title: 'Error',
        description: 'Failed to regenerate image.',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingImage(false);
    }
  };

  const handleUploadFeaturedImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !post) return;

    setUploadingFeaturedImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `blog/${post.id}/featured_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('town-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('town-images')
        .getPublicUrl(fileName);

      setFormData(prev => ({
        ...prev,
        main_image_url: publicUrl,
        image_source: 'uploaded',
        image_source_url: '',
        image_attribution_text: '',
      }));

      toast({
        title: 'Image uploaded',
        description: 'Remember to save your changes.',
      });
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setUploadingFeaturedImage(false);
      if (featuredImageInputRef.current) {
        featuredImageInputRef.current.value = '';
      }
    }
  };

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !post) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-primary" />
              <span className="font-display text-xl">Edit Post</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/blog')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </a>
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Topic */}
            <Card>
              <CardHeader>
                <CardTitle>Post Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="topic">Topic</Label>
                    <Input
                      id="topic"
                      name="topic"
                      value={formData.topic}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="excerpt">Excerpt</Label>
                  <Textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    rows={2}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Content */}
            <BlogContentEditor
              content={formData.content_html}
              onChange={(content) => setFormData(prev => ({ ...prev, content_html: content }))}
              onRegenerate={handleRegenerateContent}
              isRegenerating={regeneratingContent}
            />

            {/* Content Images & Videos */}
            <Card>
              <CardHeader>
                <CardTitle>Media Library</CardTitle>
                <CardDescription>
                  Add images and videos to use in your content. Click "Copy Embed" to get HTML code.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BlogImageGalleryManager
                  images={contentImages}
                  onChange={setContentImages}
                  storagePath={`blog/${post?.id || 'new'}`}
                  label="Content Media"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Publish</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_published">Published</Label>
                  <Switch
                    id="is_published"
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                  />
                </div>
                <div>
                  <Label htmlFor="published_at">Publish Date & Time</Label>
                  <Input
                    id="published_at"
                    name="published_at"
                    type="datetime-local"
                    value={formData.published_at}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Featured Image */}
            <Card>
              <CardHeader>
                <CardTitle>Featured Image</CardTitle>
                <CardDescription>
                  {formData.image_source === 'unsplash' && 'Sourced from Unsplash'}
                  {formData.image_source === 'pexels' && 'Sourced from Pexels'}
                  {formData.image_source === 'ai_generated' && 'AI Generated'}
                  {formData.image_source === 'uploaded' && 'Uploaded'}
                  {!formData.image_source && 'No image'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.main_image_url && (
                  <div className="relative">
                    <img 
                      src={formData.main_image_url} 
                      alt={formData.main_image_alt || 'Featured image'}
                      className="w-full rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData(prev => ({ ...prev, main_image_url: '' }))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                {formData.image_attribution_text && (
                  <p className="text-xs text-muted-foreground italic">
                    {formData.image_attribution_text}
                  </p>
                )}

                {/* Town override for image lookup */}
                <div>
                  <Label htmlFor="overrideTown">Town for Image Lookup</Label>
                  <Select 
                    value={overrideTown || "auto"} 
                    onValueChange={(v) => setOverrideTown(v === "auto" ? "" : v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Auto-detect from content" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect from content</SelectItem>
                      {towns.map(town => (
                        <SelectItem key={town.id} value={town.name}>{town.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Upload and Regenerate buttons */}
                <input
                  ref={featuredImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUploadFeaturedImage}
                  className="hidden"
                />
                <div className="flex flex-col gap-2">
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={() => featuredImageInputRef.current?.click()}
                    disabled={uploadingFeaturedImage}
                    className="w-full"
                  >
                    {uploadingFeaturedImage ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Image className="h-4 w-4 mr-2" />
                    )}
                    Upload Image
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleRegenerateImage(false)}
                    disabled={regeneratingImage}
                    className="w-full"
                  >
                    {regeneratingImage ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Real Sources
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleRegenerateImage(true)}
                    disabled={regeneratingImage}
                    className="w-full"
                  >
                    {regeneratingImage ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    AI Fallback
                  </Button>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="prefer_real_images">Prefer Real Images</Label>
                  <Switch
                    id="prefer_real_images"
                    checked={formData.prefer_real_images}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, prefer_real_images: checked }))}
                  />
                </div>

                <div>
                  <Label htmlFor="main_image_url">Image URL</Label>
                  <Input
                    id="main_image_url"
                    name="main_image_url"
                    value={formData.main_image_url}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="main_image_alt">Alt Text</Label>
                  <Input
                    id="main_image_alt"
                    name="main_image_alt"
                    value={formData.main_image_alt}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Tags */}
            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
                <CardDescription>Select relevant tags</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
                  {allTags.map(tag => (
                    <Badge
                      key={tag.id}
                      variant={selectedTagIds.includes(tag.id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}